import { useCallback, useState } from "react";

import { useAuth } from "@hl/shared-features/lib/features/auth/AuthContext";
import { TransactionState } from "@hl/shared-features/lib/features/evm-tx/TransactionContext";
import { useOnChainTransaction } from "@hl/shared-features/lib/hooks/useOnChainTransaction";
import { getEventSelector, TransactionReceipt } from "viem";

import {
  _CollectionType,
  CreateEvmMintCreatorReservesTransactionInput,
  TransactionType,
} from "apollo/graphql.generated";
import { getMintPageUrl } from "config";
import { useGetCreatorReservesTxArgsLazyQuery } from "~/features/tokens/sections/ManageToken/queries.graphql.generated";

const TRANSFER_721_EVENT_TOPIC_ID = getEventSelector(
  "Transfer(address,address,uint256)"
);
const getTokenIds = (receipt: TransactionReceipt) => {
  const mintedTokenIds: string[] = [];
  for (const log of receipt.logs) {
    if (log.topics[0] === TRANSFER_721_EVENT_TOPIC_ID) {
      mintedTokenIds.push(parseInt(log.topics[3]!, 16).toString());
    }
  }
  return mintedTokenIds;
};

type MintingToken = { id: number; imageUrl: string } | null;

const useCreatorReserves = ({
  collection,
  collectionEditionId,
  collectionType,
}: {
  collection: { id: string; onchainId: string };
  collectionEditionId: string;
  collectionType: _CollectionType;
}) => {
  const { walletAddress } = useAuth();
  const [generateTxArgs, { loading: loadingArgs, error: errorArgs }] =
    useGetCreatorReservesTxArgsLazyQuery();
  const [mintingToken, setMintingToken] = useState<MintingToken>(null);
  const collectionId = collection.id;

  const onFinish = useCallback(
    (state: TransactionState) => {
      const redirectUrl = getMintPageUrl(collection, { reveal: true });
      if (!state.receipt) return;
      const tokenIds = getTokenIds(state.receipt);
      history.pushState(
        {
          mintingToken,
          numTokensMinted: tokenIds.length,
          tokenIds: tokenIds,
          editionId: collectionEditionId,
          txHash: state.hash,
        },
        "",
        redirectUrl
      );
      history.go();
    },
    [mintingToken, collectionEditionId, collection]
  );

  const { error, startTransaction, buttonLabel, buttonLoading } =
    useOnChainTransaction({
      collectionType,
      entityId: collection.id,
      onFinish,
      refetchQueries: [],
      transactionType: TransactionType.EVM_MINT_CREATOR_RESERVES,
      finishDelay: 0,
    });

  const mint = useCallback(
    async (numberToMint: number, mintingToken: MintingToken = null) => {
      setMintingToken(mintingToken);
      const metadata: Omit<
        CreateEvmMintCreatorReservesTransactionInput,
        "from" | "hash" | "serializedTx"
      > = {
        collectionId,
        editionId: +collectionEditionId,
        numTokens: numberToMint,
        nftContractAddress: walletAddress || "",
      };
      const result = await generateTxArgs({
        variables: {
          collectionId,
          data: {
            numTokensToMint: numberToMint,
            tokenIdsToMint: mintingToken ? [mintingToken.id] : [],
          },
        },
      });
      const args = result?.data?.creatorReservesTxArgs;
      return startTransaction({ args, metadata });
    },
    [generateTxArgs, collectionId, walletAddress, collectionEditionId]
  );

  return {
    error: error || (errorArgs ? "Error preparing transaction" : ""),
    mint,
    buttonLabel,
    buttonLoading: buttonLoading || loadingArgs,
  };
};

export default useCreatorReserves;
